/**
 * ai/styles.ts —— 5 种拟人风格的量化参数（PRD **V1.7** § 2.2）
 *
 * ⚠ 同 handStrength.ts：本文件禁止引用赢率引擎与离线表（AC2.5）。
 *
 * ────────────────────────────────────────────────────────────────────
 * V1.7 阶梯（第 6 轮标定的施工依据，取代 V1.5 的「五段重叠区间」与 V1.6 的 11/24/37/50/63）
 * ────────────────────────────────────────────────────────────────────
 * VPIP 目标是一条**等间距阶梯**：中心 11 / 23 / 35 / 47 / 59，中心间距 12pp、带宽 ±1.5pp。
 * 阈值与阶梯**绑定**：判定阈值 = 中心间距 − 2 × 带宽 = 12 − 2 × 1.5 = **9.0pp**。
 * 因此「五种风格各自落带」是「相邻对差 ≥9.0pp」的充分条件（PRD AC2.2.3 → AC2.2.2 的蕴含路径），
 * 不需要再单独调参去凑间距。阶梯与阈值不得单独修改（PRD 13.4 / 14.4）。
 *
 * 唯一放行判据是 **AC2.2.2**：混桌口径下 4 对相邻风格 VPIP 差的
 * 单侧 95% 联合置信下界（Bonferroni α=0.0125、桌级 bootstrap B=2000、
 * 按「该手开始时在桌有筹码的人数」分层并用 PRD 附录 A 规范性权重加权）均 ≥9.0pp。
 * **点估计达标 ≠ 下界达标**：标定必须按置信下界留余量（工程余量目标点估计 ≥12pp）。
 *
 * ────────────────────────────────────────────────────────────────────
 * 本次（第 6 轮）旋钮取值来源
 * ────────────────────────────────────────────────────────────────────
 * 取自 PM 的 AC2.2.7 可达性实证联合标定解
 * （`docs/pm/ac22-reachability/candidate-knobs.json`，派生自 p3-calibration-softpfr.json iter7），
 * 并由研发用**验收级口径**独立种子段复测确认（`docs/fix-round6/`，脚本 `scripts/fix-round6/ac22-validate.ts`）。
 * 只动 4 个翻前阈值（openRaiseAt / limpAt / threeBetAt / callRaiseAt）；
 * betStrong / betMedium / bluffFreq / raiseStrong / raiseMedium / callAppetite / betSizing /
 * mistakeRate / jitter **全部保持出厂值不动**——动它们会连带影响 AC2.3 / AC2.4 与 AF 结构序。
 *
 * ⚠ 三条红线（PRD AC2.2.7 / round6-targets.md § 3）：
 *   ① 诈唬型 `limpAt` / `callRaiseAt` **不得推到 0**。推到 0 意味着「翻前对任何两张牌都不弃牌」，
 *      旋钮饱和 = 零调参余量，且与跟注站在「翻前弃牌率」维度上失去区分。
 *      当前取 0.082 / 0.070（第 7 轮回拉后），是参数空间**内点**，留了约两级 Chen 百分位离散台阶的余量。
 *   ② 不得为抬 VPIP 关闭或削弱 `mistakeRate` / `jitter`（AC2.3 硬约束）。
 *   ③ 阶梯只允许整体向下平移，顶端中心不得高于 60（结构性天花板 66.1、受约束可达上界 62.8±0.8）。
 *
 * ────────────────────────────────────────────────────────────────────
 * 结构性天花板（理解它才知道哪里还有空间，PRD 14.2）
 * ────────────────────────────────────────────────────────────────────
 * 单挑层（附录 A 权重 w₂ = 0.7640，判定权主体）里，小盲弃牌则大盲全程没有行动机会，
 * 诈唬型在该层约 38% 的手数「整手没轮到行动」，该层 VPIP 上限约 62.0；
 * 按附录 A 权重加权后诈唬型天花板 = 66.1。Chen 百分位只有 17 个离散档，顶端台阶粗。
 * V1.6 的顶端 63 即因此不可达（旋钮饱和后只到 61.96）。
 *
 * ⚠ 分层内自查是必须的（上一轮失效的根本教训）：V1.6 下「跟注站→诈唬」不加权口径看着有 11.6pp，
 * 但其中 6–7pp 来自桌型构成差异（辛普森效应），**分层内实际全部 ≤9.0pp**，任何权重都救不了。
 * 本轮参数每一层内的下界均 ≥9.7pp，故结论与权重口径无关（AC2.2.2 的 weightIndependent，附录 A.6）。
 *
 * 注意各旋钮之间是耦合的（松开 limpAt 会增加跟注、压低 AF；调 3-bet 阈值会同时抬 VPIP 与 PFR；
 * callAppetite 同时进入翻前跟注阈值，不是纯翻后旋钮），
 * 改动任一参数后必须重跑 `scripts/fix-round7/ac22-validate.ts` 与 `vpip-separation.test.ts`。
 *
 * ────────────────────────────────────────────────────────────────────
 * 第 7 轮改动（只动诈唬型的两个翻前跟注阈值）
 * ────────────────────────────────────────────────────────────────────
 * `maniac.limpAt` 0.064 → 0.082、`maniac.callRaiseAt` 0.052 → 0.070。
 * 依据：QA 第 3 轮遗留观察项①（诈唬型 VPIP 59.89 距带上限仅 0.61pp，建议回拉约 0.9pp）。
 * 复测证据：`docs/fix-round7/ac22-validate-final.json`（种子段 9_4xx_xxx，三批 ≥30,000 手/风格）。
 * 回拉压缩的是「跟注站→诈唬」这一对，其层内最紧的 h=2 层必须仍 ≥9.0pp —— 这是本轮的
 * 必查项，只看加权总量不算通过。
 */

/** 风格键。 */
export type StyleKey = 'tag' | 'lag' | 'rock' | 'station' | 'maniac';

/** PRD 2.2 的目标区间（验收基准，不是实现参数）。 */
export interface StyleTargets {
  readonly vpip: readonly [number, number];
  readonly pfr: readonly [number, number];
  readonly af: readonly [number, number];
  readonly bluff: readonly [number, number];
}

/** 风格定义 = 验收目标 + 实现旋钮。 */
export interface Style {
  readonly key: StyleKey;
  readonly nameCn: string;
  readonly targets: StyleTargets;

  /* ---- 翻牌前旋钮（均为「牌力百分位阈值」，越低越松）---- */
  /** 无人加注时，牌力百分位 ≥ 此值才主动加注开池（直接决定 PFR）。 */
  readonly openRaiseAt: number;
  /** 无人加注时，牌力百分位 ≥ 此值才跟注 / 跛入（与 openRaiseAt 共同决定 VPIP）。 */
  readonly limpAt: number;
  /** 面对加注时，牌力百分位 ≥ 此值才反加。 */
  readonly threeBetAt: number;
  /**
   * Round22 · F4（R4.1）加注深度阶梯阈值：面对 3-bet 时才反加（做 4-bet）的百分位阈值，逐级抬高（> threeBetAt）。
   */
  readonly fourBetAt: number;
  /**
   * Round22 · F4（R4.1）：面对 4-bet+ 时才反加（做 5-bet 及更深）的百分位阈值，最高（> fourBetAt）。
   * maniac 也要求前 ~20% 强牌（≥0.80），根治「深度盲区导致任何牌都反加」。
   */
  readonly fiveBetAtPlus: number;
  /** 面对加注时，牌力百分位 ≥ 此值才跟注。 */
  readonly callRaiseAt: number;
  /**
   * Round23 · BUG-R01（AC-5.5 AF 序）：**开池跛入→开池加注**的升级概率。
   * 仅在「无人加注 + 本可跛入（limpAt ≤ 百分位 < openRaiseAt）」时生效：以此概率把跛入（call）
   * 升级为主动开池加注（raise）。默认 0（其余 persona 完全不受影响，行为逐字节不变）。
   *
   * 背景：诈唬型（maniac）出厂标定把 51% 的入池手做成「跛入」，跛入=翻前 call，把「全街 AF＝
   * (bet+raise)/call」摊薄到 2.16，反而低于松凶（lag 3.71）——与「maniac 最上」的画像矛盾
   * （真人 maniac 入池即加注、极少跛入）。本旋钮把 maniac 的跛入按比例改成开池加注，使其
   * **全 persona AF 最高**（AC-5.5），同时不动 VPIP（跛入/加注同属自愿入池，梯度与相邻 ≥9pp 不变）、
   * 不动翻前阈值（openRaiseAt/limpAt/threeBetAt/callRaiseAt）与翻后旋钮。
   */
  readonly openLimpToRaise: number;

  /* ---- 翻牌后旋钮 ---- */
  /** 持强牌时主动下注的概率。 */
  readonly betStrong: number;
  /** 持中等牌时主动下注的概率。 */
  readonly betMedium: number;
  /** 用弱牌 / 听牌发起诈唬下注的概率（对应 PRD 的「诈唬率」）。 */
  readonly bluffFreq: number;
  /** 面对下注时，持强牌加注的概率（AF 的主要来源）。 */
  readonly raiseStrong: number;
  /** 面对下注时，持中等牌加注的概率。 */
  readonly raiseMedium: number;
  /** 跟注意愿系数：越大越容易用弱牌跟注（跟注站的核心特征，会压低 AF）。 */
  readonly callAppetite: number;
  /** 下注尺度（占底池比例）。 */
  readonly betSizing: readonly [number, number];

  /* ---- Round23 · FR-5 翻牌后旋钮（赔率驱动跟注 / 概率化诈唬）---- */
  /**
   * 赔率偏移（persona 对「所需底池赔率」的松紧）：所需胜率 = potOdds − oddsBias。
   * 正值更松（跟注站/松凶偏正，会在略不划算时也跟）、负值更紧（保守要求超过赔率才跟）。
   * 是 AC-5.3（烂牌跟注上限）/ AC-5.4（赔率合规）/ AC-5.5（AF 序）的主旋钮，**不进翻前分支**。
   */
  readonly oddsBias: number;

  /* ---- 拟人化旋钮（PRD 2.1 第 ③ 项）---- */
  /** 概率化犯错的基础概率。 */
  readonly mistakeRate: number;
  /** 各阈值的随机扰动幅度（百分位单位），保证同局面多次决策不完全相同（AC2.3）。 */
  readonly jitter: number;
}

/**
 * 5 种风格。
 *
 * 阈值与 VPIP / PFR 的关系（这是调参能收敛的原因）：
 *   PFR  ≈ 1 − openRaiseAt        （无人加注局面下会加注的手牌比例）
 *   VPIP ≈ 1 − limpAt             （无人加注局面下会入池的手牌比例）
 * 实际值会因为「面对加注被迫弃牌」而略低于上式，因此 limpAt / openRaiseAt
 * 取的是比理论值略松一点的数，最终由验收级跑批实测校准
 * （第 6 轮：`scripts/fix-round6/ac22-validate.ts`，证据 `docs/fix-round6/`）。
 */
export const STYLES: Readonly<Record<StyleKey, Style>> = {
  // 紧凶：入池少但入池即主动。V1.7 中心 23（+2.2pp，与保守拉开间距的另一半）
  tag: {
    key: 'tag', nameCn: '紧凶型',
    targets: { vpip: [21.5, 24.5], pfr: [15, 21], af: [3.0, 4.5], bluff: [8, 15] },
    openRaiseAt: 0.767, limpAt: 0.684, threeBetAt: 0.701, callRaiseAt: 0.675,
    fourBetAt: 0.820, fiveBetAtPlus: 0.900, openLimpToRaise: 0,
    betStrong: 0.80, betMedium: 0.40, bluffFreq: 0.16, raiseStrong: 0.58, raiseMedium: 0.13,
    callAppetite: 1.15, betSizing: [0.5, 0.75], oddsBias: 0.00, mistakeRate: 0.04, jitter: 0.035,
  },
  // 松凶：入池多且高频加注。V1.7 中心 35（+2.9pp），PFR 带随中心 37→35 按比例下移到 28–34
  lag: {
    key: 'lag', nameCn: '松凶型',
    targets: { vpip: [33.5, 36.5], pfr: [28, 34], af: [5.0, 7.5], bluff: [20, 30] },
    openRaiseAt: 0.571, limpAt: 0.477, threeBetAt: 0.538, callRaiseAt: 0.469,
    fourBetAt: 0.720, fiveBetAtPlus: 0.860, openLimpToRaise: 0,
    betStrong: 0.86, betMedium: 0.45, bluffFreq: 0.27, raiseStrong: 0.60, raiseMedium: 0.20,
    callAppetite: 1.35, betSizing: [0.55, 0.9], oddsBias: 0.04, mistakeRate: 0.05, jitter: 0.045,
  },
  // 保守：只玩强牌，遇加注易弃。V1.7 中心 11（−1.2pp；不可再往下压，否则 PFR 掉出 5–11 带、AF 破 1.0）
  rock: {
    key: 'rock', nameCn: '保守型',
    targets: { vpip: [9.5, 12.5], pfr: [5, 11], af: [1.2, 2.2], bluff: [3, 8] },
    openRaiseAt: 0.893, limpAt: 0.889, threeBetAt: 0.974, callRaiseAt: 0.902,
    fourBetAt: 0.985, fiveBetAtPlus: 0.995, openLimpToRaise: 0,
    betStrong: 0.24, betMedium: 0.06, bluffFreq: 0.075, raiseStrong: 0.035, raiseMedium: 0.012,
    callAppetite: 1.45, betSizing: [0.45, 0.6], oddsBias: -0.03, mistakeRate: 0.03, jitter: 0.03,
  },
  // 跟注站：爱跟注不爱加注（AF 必须 <1，所以 raise 概率要压得很低）。
  // V1.7 中心 47，基本保持；⚠ 不要往下压——会同时挤紧「松凶→跟注站」那一对。
  station: {
    key: 'station', nameCn: '跟注站',
    targets: { vpip: [45.5, 48.5], pfr: [4, 10], af: [0.3, 0.9], bluff: [2, 7] },
    openRaiseAt: 0.888, limpAt: 0.270, threeBetAt: 0.968, callRaiseAt: 0.278,
    fourBetAt: 0.982, fiveBetAtPlus: 0.994, openLimpToRaise: 0,
    betStrong: 0.46, betMedium: 0.10, bluffFreq: 0.018, raiseStrong: 0.24, raiseMedium: 0.06,
    callAppetite: 1.35, betSizing: [0.35, 0.5], oddsBias: 0.10, mistakeRate: 0.06, jitter: 0.04,
  },
  // 诈唬型：高频施压。V1.7 中心 59（+7.3pp，本轮最大移动量；「跟注站→诈唬」全靠抬这一端解决）
  // ⚠ 红线：limpAt / callRaiseAt 必须保持非 0（内点）。推到 0 时 VPIP 也只到 61.96，
  //   且「翻前永不弃牌」会让它与跟注站在翻前弃牌率上失去区分（PRD AC2.2.7 / 14.3）。
  maniac: {
    key: 'maniac', nameCn: '诈唬型',
    targets: { vpip: [57.5, 60.5], pfr: [34, 42], af: [2.2, 3.6], bluff: [35, 55] },
    /**
     * 第 7 轮回拉：`limpAt` 0.064 → 0.082、`callRaiseAt` 0.052 → 0.070（同步 +0.018）。
     * 原因：QA 第 3 轮实测诈唬型 VPIP 59.89，距带上限 60.5 只剩 0.61pp（中心偏高 +0.89pp，
     * 95% CI 上端 60.14 已进入带的最后 0.36pp），换个种子段就有实际概率被推出带外，
     * 会让 AC2.2.3 的「5/5 落带」快路径掉成标定漂移项。按 QA 处方「limpAt 略上调、仍是内点」
     * 把 VPIP 回拉约 0.9pp 到中心 59 附近。灵敏度实测 ≈ −50pp / 单位 limpAt
     * （`docs/fix-round7/vpip-pullback-inner.json`，公共随机数内环，5 个候选 × 20,000 手）。
     * `openRaiseAt` / `threeBetAt` **不动**：PFR 37.35 已在 34–42 带中部，不需要跟着下移。
     * 两个旋钮仍为内点（0.082 / 0.070，红线①），且远低于跟注站的 0.270 / 0.278（可区分性）。
     *
     * ⚠ Round22 · F4（R4.4）：`threeBetAt` 0.095 → **0.350**（过滤翻前极差牌反加，根治根因 A
     * 「叠加 shift 后近乎任何牌都 3-bet」）。开池 `openRaiseAt` 不动、`limpAt`/`callRaiseAt` 不动，
     * 故 VPIP 主驱动（开池/跛入）不变；差牌弃牌改由「深度阶梯阈值 + preflop pressure 因子 + all-in
     * 护栏」（见 decision.ts）承担。新增 `fourBetAt`/`fiveBetAtPlus` 逐级抬高（0.620/0.820，
     * 5-bet+ 要求前 ~18% 强牌，AC4.6）。改后须复跑 `vpip-separation.test.ts`（PFR/VPIP≥0.50、
     * VPIP 落带、相邻 ≥8pp）与 `round6-ladder.test.ts` 确认不破坏 AC2.2。
     */
    openRaiseAt: 0.560, limpAt: 0.082, threeBetAt: 0.350, callRaiseAt: 0.070,
    fourBetAt: 0.620, fiveBetAtPlus: 0.820, openLimpToRaise: 0.80,
    betStrong: 0.99, betMedium: 0.97, bluffFreq: 0.54, raiseStrong: 0.99, raiseMedium: 0.55,
    callAppetite: 0.15, betSizing: [0.66, 1.0], oddsBias: 0.06, mistakeRate: 0.07, jitter: 0.055,
  },
};

/** 全部风格键，顺序固定，便于随机分配与快照。 */
export const STYLE_KEYS: readonly StyleKey[] = ['tag', 'lag', 'rock', 'station', 'maniac'];

/** 取风格定义。 */
export function getStyle(key: StyleKey): Style {
  const s = STYLES[key];
  if (!s) throw new Error(`未知 AI 风格：${key}`);
  return s;
}
